'use client';

import { useRef } from 'react';

import { useGSAP } from '@gsap/react';
import cx from 'classnames';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { debounce } from 'lodash';

import { type Masked, unmask } from '~/contentful/graphql';
import { flattenCollection } from '~/contentful/utils/collection';
import { Column, type ColumnProps, Grid } from '~/ui/components/grid';
import { useBreakpoint } from '~/ui/components/grid/useBreakpoint';
import { Image } from '~/ui/modules/image';
import { type Image_AssetFragment } from '~/ui/modules/image/query';
import { Breakpoint, MediaQuery } from '~/ui/styles/grid';
import gridStyles from '~/ui/styles/grid.module.scss';
import typographyStyles from '~/ui/styles/typography.module.scss';

import styles from './parallaxImages.module.scss';
import { type ParallaxImages_ParallaxImagesQuery } from './query';

gsap.registerPlugin(ScrollTrigger, useGSAP);

export interface ParallaxImagesProps {
  data: Masked<typeof ParallaxImages_ParallaxImagesQuery>;
  className?: string;
}

export function ParallaxImages({ data, className }: ParallaxImagesProps) {
  const { body, imagesCollection } = unmask<typeof ParallaxImages_ParallaxImagesQuery>(data);
  const images = flattenCollection(imagesCollection);

  const ref = useRef<HTMLDivElement>(null);
  const isMobile = useBreakpoint(Breakpoint.MOBILE);

  useGSAP(() => {
    const onResize = debounce(() => {
      ScrollTrigger.refresh(true);
      ScrollTrigger.sort();
    });
    gsap.delayedCall(1, onResize);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useGSAP(
    () => {
      const mm = gsap.matchMedia();
      mm.add(
        {
          isMd: `${MediaQuery[Breakpoint.TABLET]} and (prefers-reduced-motion: no-preference)`,
          isLg: `${MediaQuery[Breakpoint.DESKTOP]} and (prefers-reduced-motion: no-preference)`,
        },
        () => {
          const root = ref.current;
          if (!root) {
            return;
          }

          const body = root.querySelector(`.${styles.body}`) as HTMLDivElement;
          gsap.set(root, {
            paddingBottom: body.offsetHeight + parseFloat(getComputedStyle(root).marginBottom),
          });
          gsap
            .timeline({
              scrollTrigger: {
                trigger: `.${styles.body}`,
                start: 'center center',
                end: self => `+=${root.clientHeight - (self.trigger?.clientHeight ?? 0)}`,
                pin: true,
                invalidateOnRefresh: true,
                scrub: true,
              },
            })
            .to(
              `.${styles.body}`,
              {
                opacity: 0.2,
                duration: 0.5,
                ease: 'none',
              },
              0,
            )
            .to(
              `.${styles.image}`,
              {
                y: (_, target) => {
                  const img = target.querySelector('img') as HTMLImageElement;
                  const speed = 1 - parseFloat(img.getAttribute('data-speed') ?? '1');
                  return speed * 100;
                },
              },
              0,
            )
            .to(
              `.${styles.body}`,
              {
                opacity: 1,
                duration: 0.5,
                ease: 'none',
              },
              '+=3',
            );
        },
      );
      return () => mm.revert();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    {
      scope: ref,
      dependencies: [],
    },
  );

  return (
    <>
      <Grid className={cx(styles.root, className)} ref={ref}>
        <Column
          span={{ [Breakpoint.MOBILE]: 5, [Breakpoint.TABLET]: 5, [Breakpoint.DESKTOP]: 5 }}
          offsetLeft={{ [Breakpoint.TABLET]: 1, [Breakpoint.DESKTOP]: 2 }}
          className={styles.bodyColumn}
        >
          <p className={cx(styles.body, typographyStyles.modulesTitle)}>{body}</p>
        </Column>
        {isMobile ? (
          images.map((image, index) => (
            <Column
              span={{ [Breakpoint.MOBILE]: 3 }}
              offsetLeft={{
                [Breakpoint.MOBILE]: images.length > 1 ? (index % 2 === 0 ? 0 : 2) : 1,
              }}
              className={styles.imageColumn}
              key={index}
            >
              <div
                className={cx({
                  [gridStyles.overflowLeft]: images.length > 1 && index % 2 === 0,
                  [gridStyles.overflowRight]: images.length > 1 && index % 2 === 1,
                })}
              >
                <Image
                  image={image}
                  className={styles.image}
                  aspectRatio={{ portrait: '4/5', landscape: '3/2' }}
                />
              </div>
            </Column>
          ))
        ) : (
          <Images images={images} />
        )}
      </Grid>
    </>
  );
}

function Images({ images }: { images: Masked<typeof Image_AssetFragment>[] }) {
  switch (images.length) {
    case 1:
      return (
        <ParallaxImageColumn
          span={{ [Breakpoint.TABLET]: 5, [Breakpoint.DESKTOP]: 5 }}
          offsetLeft={{ [Breakpoint.TABLET]: 1, [Breakpoint.DESKTOP]: 2 }}
        >
          <ParallaxImage image={images[0]} speed={1} />
        </ParallaxImageColumn>
      );

    case 2:
      return (
        <>
          <ParallaxImageColumn span={{ [Breakpoint.TABLET]: 4, [Breakpoint.DESKTOP]: 5 }}>
            <ParallaxImage image={images[0]} speed={0.5} />
          </ParallaxImageColumn>
          <ParallaxImageColumn
            span={{ [Breakpoint.TABLET]: 2, [Breakpoint.DESKTOP]: 3 }}
            offsetLeft={{ [Breakpoint.TABLET]: 1, [Breakpoint.DESKTOP]: 1 }}
          >
            <ParallaxImage image={images[1]} speed={1} />
          </ParallaxImageColumn>
        </>
      );

    case 3:
      return (
        <>
          <ParallaxImageColumn span={{ [Breakpoint.TABLET]: 2, [Breakpoint.DESKTOP]: 3 }}>
            <ParallaxImage image={images[1]} speed={1.5} />
            <ParallaxImage image={images[2]} speed={1} />
          </ParallaxImageColumn>
          <ParallaxImageColumn
            span={{ [Breakpoint.TABLET]: 4, [Breakpoint.DESKTOP]: 5 }}
            offsetLeft={{ [Breakpoint.TABLET]: 1, [Breakpoint.DESKTOP]: 1 }}
          >
            <ParallaxImage image={images[0]} speed={0.75} />
          </ParallaxImageColumn>
        </>
      );

    case 4:
      return (
        <>
          <ParallaxImageColumn span={{ [Breakpoint.TABLET]: 4, [Breakpoint.DESKTOP]: 5 }}>
            <ParallaxImage image={images[0]} speed={1.2} />
            <ParallaxImage image={images[2]} speed={1} />
          </ParallaxImageColumn>
          <ParallaxImageColumn
            span={{ [Breakpoint.TABLET]: 2, [Breakpoint.DESKTOP]: 3 }}
            offsetLeft={{ [Breakpoint.TABLET]: 1, [Breakpoint.DESKTOP]: 1 }}
          >
            <ParallaxImage image={images[1]} speed={2} />
            <ParallaxImage image={images[3]} speed={1.5} />
          </ParallaxImageColumn>
        </>
      );

    case 5:
      return (
        <>
          <ParallaxImageColumn
            span={{ [Breakpoint.TABLET]: 5, [Breakpoint.DESKTOP]: 5 }}
            offsetLeft={{ [Breakpoint.TABLET]: 1, [Breakpoint.DESKTOP]: 2 }}
          >
            <ParallaxImage image={images[0]} speed={1.4} />
          </ParallaxImageColumn>
          <ParallaxImageColumn span={{ [Breakpoint.TABLET]: 2, [Breakpoint.DESKTOP]: 3 }}>
            <ParallaxImage image={images[1]} speed={1.4} />
            <ParallaxImage image={images[3]} speed={0.8} />
          </ParallaxImageColumn>
          <ParallaxImageColumn
            span={{ [Breakpoint.TABLET]: 4, [Breakpoint.DESKTOP]: 5 }}
            offsetLeft={{ [Breakpoint.TABLET]: 1, [Breakpoint.DESKTOP]: 1 }}
          >
            <ParallaxImage image={images[2]} speed={1.2} />
            <Grid>
              <ParallaxImageColumn span={{ [Breakpoint.TABLET]: 3, [Breakpoint.DESKTOP]: 4 }}>
                <ParallaxImage image={images[4]} speed={1} />
              </ParallaxImageColumn>
            </Grid>
          </ParallaxImageColumn>
        </>
      );

    case 6:
      return (
        <>
          <ParallaxImageColumn span={{ [Breakpoint.TABLET]: 2, [Breakpoint.DESKTOP]: 3 }}>
            <Grid overflow>
              <ParallaxImageColumn span={{ [Breakpoint.TABLET]: 4, [Breakpoint.DESKTOP]: 5 }}>
                <ParallaxImage image={images[0]} speed={2} />
              </ParallaxImageColumn>
            </Grid>
            <ParallaxImage image={images[3]} speed={1.5} />
            <ParallaxImage image={images[5]} speed={1.25} />
          </ParallaxImageColumn>
          <ParallaxImageColumn
            span={{ [Breakpoint.TABLET]: 4, [Breakpoint.DESKTOP]: 5 }}
            offsetLeft={{ [Breakpoint.TABLET]: 1, [Breakpoint.DESKTOP]: 1 }}
          >
            <Grid>
              <ParallaxImageColumn
                span={{ [Breakpoint.TABLET]: 2, [Breakpoint.DESKTOP]: 3 }}
                offsetLeft={{ [Breakpoint.TABLET]: 2, [Breakpoint.DESKTOP]: 2 }}
              >
                <ParallaxImage image={images[1]} speed={1} />
                <ParallaxImage image={images[2]} speed={1} />
              </ParallaxImageColumn>
            </Grid>
            <ParallaxImage image={images[4]} speed={1} />
          </ParallaxImageColumn>
        </>
      );
  }
}

function ParallaxImageColumn({ children, className, ...props }: ColumnProps) {
  return (
    <Column {...props} className={cx(styles.imageColumn, className)}>
      {children}
    </Column>
  );
}

function ParallaxImage({
  image,
  speed,
}: {
  image: Masked<typeof Image_AssetFragment>;
  speed: number;
}) {
  return (
    <Image
      image={image}
      data-speed={speed}
      className={styles.image}
      aspectRatio={{
        portrait: '4/5',
        landscape: '3/2',
      }}
    />
  );
}
